import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import {getGlobalUseRouter, md5OfFile} from "@/utils";
import {setFindItemId} from "@/utils/menu";
import axios from "axios";
import {
    setMakeAllSearchData,
} from "@/utils/homepage";

/**
 * 템플릿 HTML 변환 결과
 * @typedef {object} TemplatePageResult
 * @property {string} tabHtml - 탭 영역 HTML
 * @property {string} contentHtml - 컨텐츠 영역 HTML
 */

/**
 * @typedef {object} SearchMap
 * @property {string} searchType
 * @property {string} searchString
 * @property {number} offset
 */

/**
 * 홈페이지 컨텐츠 관련 Store
 */
export class ContentStore {
    /**
     * 공통 검색 관련 Store
     * @type {SearchBaseStore}
     */
    searchBaseStore;
    /**
     * FORM 타입 페이지에서 사용하는 입력 데이터 맵
     * @type {Object}
     */
    formDataMap = {}
    /**
     * FORM 타입 페이지에서 업로드한 파일 리스트
     * @type {Array<File|Object|string>}
     */
    formFile = [];
    /**
     * TEMPLATE 타입 페이지 전체 HTML 데이터
     * @type {string|Object|null}
     */
    templateHtml = null;
    /**
     * TEMPLATE 타입 페이지 탭 HTML 데이터
     * @type {string|Object|null}
     */
    templateTabHtml= null;
    /**
     * TEMPLATE 타입 페이지 컨텐츠 HTML 데이터
     * @type {string|Object|null}
     */
    templateContentHtml= null;
    /**
     * 메인 페이지 뉴스 그룹 데이터 리스트
     * @type {Array<Object>}
     */
    newsGroupList = [];
    /**
     * 메인 페이지 파트너 리스트 데이터
     * @type {Array<Object>}
     */
    partnersList = [];
    /**
     * 메인 페이지 Career 데이터 리스트
     * @type {Array<Object>}
     */
    careerDataList = [];
    /**
     * BOARD 타입 공통 리스트 데이터
     * @type {Array<Object>}
     */
    homepageDataList = [];
    /**
     * BOARD 타입 공통 리스트 총 개수
     * @type {number}
     */
    homepageDataCount = 0;
    /**
     * BOARD 타입 공통 검색 파라미터
     * @type {SearchMap}
     */
    searchMap = {
        searchType: "title",
        searchString: "",
        offset: 0
    };
    /**
     * 현재 선택된 메뉴 데이터 정보
     * @type {string|Object|null}
     */
    currentDataInfo = null;
    /**
     * BOARD 상세 데이터
     * @type {string|Object|null}
     */
    boardDetailData = null;
    /**
     * BOARD 상세 다음글 데이터
     * @type {string|Object|null}
     */
    boardDetailNextData = null;
    /**
     * BOARD 상세 이전글 데이터
     * @type {string|Object|null}
     */
    boardDetailPrevData = null;
    /**
     * 우측 플로팅 메뉴 문의하기 리스트
     * @type {Array<Object>}
     */
    floatContactMenu = [];
    /**
     * 우측 플로팅 메뉴 신청하기 리스트
     * @type {Array<Object>}
     */
    floatApplyMenu = [];
    /**
     * 전체 검색 페이지 메뉴 리스트
     * @type {Array<Object>}
     */
    searchRootMenuList = [];

    constructor(store) {
        this.store = store;
        this.searchBaseStore = store.searchBaseStore;
        makeAutoObservable(this, {}, {autoBind: true});
    }




    /**
     * BOARD 타입 리스트 데이터를 조회하는 함수 검색 파라미터 변환 및 전체 검색 타입 처리 포함
     *
     * @param {string} params URL Query String
     * @param {string|undefined} type 조회 타입
     * @param {Array|null} menuList 전체 검색 메뉴 리스트
     * @returns {Promise<void>}
     */
    async getFetchBoard(params, type = undefined, menuList = null) {
        const search = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
        if (search.get("menu-id")) {
            search.set("menuId", search.get("menu-id"));
            search.delete("menu-id");
        }
        if(search.get("root-id")){
            search.set("rootMenuId", search.get("root-id"));
            search.delete("root-id");
        }
        if (search.get("searchType")) {
            if(search.get("searchType") === "title"){
                search.set("searchType", "T");
            } else if(search.get("searchType") === "content"){
                search.set("searchType", "C");
            }
        }
        if (!search.get("searchString")) {
            search.delete("searchType");
        }
        if(type === "allSearch"){
            if(search.get("searchString")){
                search.set("searchType", "TC");
            }
        }
        search.delete("cooperation-id");
        search.set("active", "active");
        const finalParams = `?${search.toString()}`;
        const response = await axios.get(`/api/content${finalParams}`);
        if(response){
            runInAction(() => {
                this.homepageDataList = response.data.data.contents;
                this.homepageDataCount = response.data.data.totalCount;
            });
            if(type === "allSearch"){
                if(response.data.data?.menuCount){
                    const list = setMakeAllSearchData(menuList, response.data.data.menuCount);
                    console.log(list, "list")
                    let totalCount = 0;
                    for (const key in response.data.data.menuCount) {
                        totalCount += response.data.data.menuCount[key];
                    }
                    this.searchRootMenuList = [
                        {
                            title: "전체",
                            count: totalCount,
                            menuId: null,
                        },
                        ...list,
                    ];
                }
            }
        }
    }

    /**
     * 현재 메뉴 정보를 기준으로 타입별 데이터 세팅 함수
     * TEMPLATE / FORM / BOARD 타입 분기 처리
     * 메뉴가 존재하지 않을 경우 404 페이지 이동
     *
     * @param {Array} list 전체 메뉴 리스트
     * @param {string} id 현재 메뉴 ID
     * @param {string} params URL Query String
     * @param {string|undefined} contentId BOARD 상세 컨텐츠 ID
     * @returns {Promise<void>}
     */
    async setMenuContent(list, id, params, contentId = undefined) {
        this.setClearBoard();
        this.searchBaseStore.clearBase();
        this.currentDataInfo = setFindItemId(list, id);
        if(!this.currentDataInfo){
            const router = getGlobalUseRouter();
            router?.push(`/404`);
            return;
        }
        if(this.currentDataInfo.type === "TEMPLATE"){
            this.setTemplateClear();
            await this.setTemplateData(id);
        } else if(this.currentDataInfo.type === "FORM"){
            const contentItem = this.currentDataInfo.form.find(item => item.type === "content");
            this.setFormDataUpDate(contentItem.value, contentItem._id)
        } else {
            if(params){
                if(this.currentDataInfo.type === "BOARD" && this.currentDataInfo.subType !== "COOPERATION"){
                    if (this.currentDataInfo.options.limit) {
                        this.searchMap["limit"] = this.currentDataInfo.options.limit;
                    }
                }
                const flatParams = this.searchBaseStore.setCurrentParams(params, this.searchMap);
                await this.getFetchBoard(flatParams);
            }
            if(contentId){
                this.setClearBoardDetail();
                await this.getBoardDetailData(contentId);
            }
        }
    }




    /**
     * BOARD 상세 페이지 데이터 조회 함수
     * 상세 / 이전글 / 다음글 데이터를 세팅
     *
     * @param {string} id 컨텐츠 ID
     * @returns {Promise<void>}
     */
    async getBoardDetailData(id){
        const response = await axios.get(`/api/content/detail?contentId=${id}`);
        const resData = response.data.data.content;
        if(resData){
            this.boardDetailData = resData;
        }
        if(response.data.data.next){
            this.boardDetailNextData = response.data.data.next;
        }

        if(response.data.data.prev){
            this.boardDetailPrevData = response.data.data.prev;
        }
    }




    /**
     * 메인 페이지 Career 리스트 데이터 조회 함수
     *
     * @param {number} limit 조회 개수 제한
     * @returns {Promise<void>}
     */
    async setFetchBoardCareerList(limit) {
        const response = await axios.get(`/api/content?menuId=68dcf802655492292e882ce7&limit=${limit}&active=active`);
        if(response){
            runInAction(() => {
                this.careerDataList = response.data.data.contents;
            });
        }
    }


    /**
     * 메인 페이지 파트너사 리스트 데이터 조회 함수
     *
     * @returns {Promise<void>}
     */
    async setFetchBoardPartnersList() {
        const response = await axios.get(`/api/content?menuId=6911679dc7ed96d6e4edf6fa&limit=100&active=active`);
        if(response){
            runInAction(() => {
                this.partnersList = response.data.data.contents.filter(item => item?.data?.isActive === "active").map(item => ({
                    imageItem: item?.data?.files?.[0]?.path || null,
                }));
            });
        }
    }

    /**
     * 메인 페이지 뉴스 컨텐츠 데이터 조회 함수
     * 하위 메뉴별 뉴스 데이터를 그룹화하여 생성
     *
     * @param {Array} list 전체 메뉴 리스트
     * @param {string} id 부모 메뉴 ID
     * @returns {Promise<Array>}
     */
    async getNewsContentsData(list, id) {
        const result = [];
        const parent = setFindItemId(list, id);
        if (!parent || !Array.isArray(parent.children)) return [];
        const children = parent.children.filter(item => item?.options?.isActive).map(child => ({
            menuId: child.menuId,
            title: child.title,
        }));
        for (const item of children) {
            const response = await axios.get(`/api/content?menuId=${item.menuId}&limit=10&active=active`);
            result.push({
                menuId: item.menuId,
                title: item.title,
                data: response.data,
            });
        }
        this.newsGroupList = result;
    }

    /**
     * FORM 입력 데이터 업데이트 함수
     *
     * @param {*} value 저장할 값
     * @param {string|undefined} id FORM ROW ID
     * @param {string} sub 서브 키값
     */
    setFormDataUpDate(value, id = undefined, sub = ""){
        if(this.currentDataInfo){
            const row = this.currentDataInfo.form.find(item => item._id === id);
            if(sub){
                this.formDataMap[`${row.type}-${sub}-${id}`] = value;
            } else {
                this.formDataMap[`row${id}`] = value;
            }
        }
    }

    /**
     * FORM 업로드 파일 리스트 세팅 함수
     *
     * @param {Array<File>} files 업로드 파일 리스트
     */
    setFormFiles(files = []) {
        this.formFile = files;
    }


    /**
     * FORM 업로드 파일 삭제 함수
     *
     * @param {number} index 삭제할 파일 인덱스
     */
    removeFormFile(index) {
        const next = [...this.formFile];
        next.splice(index, 1);
        this.formFile = next;
    }

    /**
     * FORM 타입 데이터 저장 함수
     * 파일 업로드 및 MD5 해시 생성 포함
     *
     * @param {string} menuId 메뉴 ID
     * @param {string} rootId 루트 메뉴 ID
     * @param {Object} contentData form 작성 페이지 보낼 데이터 백엔드 형식으로 변환
     * @returns {Promise<AxiosResponse|undefined>}
     */
    async setSaveFormData(menuId, rootId, contentData) {
        if(this.currentDataInfo){
            const formData = new FormData();
            const contentInner = this.currentDataInfo.type === "FORM" ? " " : ""
            formData.append("menuId", menuId);
            formData.append("rootMenuId", rootId);
            formData.append("type", this.currentDataInfo.type);
            formData.append("subType", this.currentDataInfo.subType);
            formData.append("data", JSON.stringify(contentData));
            for (const file of this.formFile) {
                formData.append("files", file);
            }
            const md5List = await Promise.all(this.formFile.map(file => md5OfFile(file)));
            const fileInfos = md5List.map((hash, i) => ({
                md5: hash,
                name: this.formFile[i].name,
            }));
            if(fileInfos && fileInfos.length > 0){
                formData.append("fileInfos", JSON.stringify(fileInfos));
            }
            formData.append("content", contentInner);
            return await axios.post("/api/content/form", formData)
        }
    }


    /**
     * TEMPLATE 타입 페이지 데이터 조회 함수
     *
     * @param {string} id 메뉴 ID
     * @returns {Promise<void>}
     */
    async setTemplateData(id){
        const response = await axios.get(`/api/content?menuId=${id}`);
        if(response.data.data && response.data.data.contents && response.data.data.contents.length > 0){
            const dataHtml = response.data.data.contents[response.data.data.contents.length - 1].data;
            let { tabHtml, contentHtml } = this.setMakeTemplatePage(dataHtml);
            // TODO: 2026-05-13 1번 내용 필요시 추가
            // contentHtml = this.convertTextNodeNewLineToBr(contentHtml);
            this.templateTabHtml = tabHtml;
            this.templateContentHtml = contentHtml;
        }
    }

    /**
     * 홈페이지에 노출될 TEMPLATE 타입 페이지 HTML 데이터 생성
     * @param {string} htmlString - 원본 HTML 문자열
     * @returns {TemplatePageResult}
     */
    setMakeTemplatePage(htmlString) {
        if (!htmlString) return { tabHtml: "", contentHtml: "" };
        const wrap = document.createElement("div");
        wrap.innerHTML = htmlString;
        const tabWrap = wrap.querySelector(".control-tab-wrap");
        const contentWrap = wrap.querySelector(".module-section");
        let tabHtml = "";
        let contentHtml = contentWrap ? contentWrap.outerHTML : "";
        if (tabWrap) {
            const tabs = Array.from(tabWrap.querySelectorAll(".tabs-item"));
            tabHtml = `
            <div class="tab-swiper-nav-wrap">
                <div class="tab-swiper-prev"></div>
                <div class="control-tab-wrap swiper tab-swiper">
                    <div class="swiper-wrapper">
                        ${tabs.map((tab) =>
                `<div class="swiper-slide tabs-item ${tab.classList.contains("active") ? "active" : ""}" data-template-module-tab="${tab.getAttribute("data-template-module-tab") || ""}">
                                ${tab.innerHTML}
                            </div>`).join("")}
                    </div>
                </div>
                <div class="tab-swiper-next"></div>
            </div>
        `;
        }
        return {tabHtml, contentHtml};
    }

    /**
     * 홈페이지에 노출될 TEMPLATE 타입 페이지 HTML 내부 TextNode 개행(\n)을 br 태그로 변환
     * @param {string} html - HTML 문자열
     * @returns {string}
     */
    convertTextNodeNewLineToBr(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const textNodes = [];
        const walker = document.createTreeWalker(
            doc.body,
            NodeFilter.SHOW_TEXT
        );
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            textNodes.push(node);
        }
        textNodes.forEach((node) => {
            const text = node.textContent;
            if (!text.includes("\n")) return;
            if (text.trim() === "") return;
            const lines = text.split("\n");
            const cleanedLines = lines.map((line) => line.trimEnd());
            const fragment = document.createDocumentFragment();
            cleanedLines.forEach((line, idx) => {
                fragment.appendChild(document.createTextNode(line));
                if (idx !== cleanedLines.length - 1) {
                    fragment.appendChild(
                        document.createElement("br")
                    );
                }
            });
            node.parentNode.replaceChild(fragment, node);
        });
        return doc.body.innerHTML;
    }




    /**
     * 우측 플로팅 메뉴 데이터 세팅 함수
     * 문의하기 / 신청하기 메뉴 분리 처리
     *
     * @param {Array} list 전체 메뉴 리스트
     */
    setFloatFormMenu(list) {
        const contact = [];
        const apply = [];
        const walk = (nodes) => {
            if (!Array.isArray(nodes)) return;
            for (let i = 0; i < nodes.length; i++) {
                const item = nodes[i];
                if (item.type === "FORM" && item.subType && item.options && item.options.floatMenu === true) {
                    if (item.subType === "CONTACT") {
                        contact.push(item);
                    } else if (item.subType === "APPLY") {
                        apply.push(item);
                    }
                }
                if (item.children && item.children.length > 0) {
                    walk(item.children);
                }
            }
        };
        walk(list);
        this.floatContactMenu = contact;
        this.floatApplyMenu = apply;
    }




    /**
     * 공통 검색 파라미터 초기화 함수
     *
     * @param {Object} param 초기화할 파라미터
     */
    clearParams(param) {
        this.searchBaseStore.searchParams = param;
    }

    /**
     * BOARD 리스트 페이지 데이터 초기화 함수
     */
    clearSearchPage() {
        this.homepageDataList = [];
        this.homepageDataCount = 0;
        this.searchRootMenuList = [];
    }


    /**
     * BOARD 상세 페이지 데이터 초기화 함수
     */
    setClearBoardDetail() {
        this.boardDetailData = null;
        this.boardDetailNextData = null;
        this.boardDetailPrevData = null;
    }

    /**
     * BOARD 타입 페이지 데이터 초기화 함수
     */
    setClearBoard() {
        this.homepageDataList = [];
        this.homepageDataCount = [];
        this.searchMap = {
            searchType: "",
            searchString: "",
            offset: 0
        };
        this.currentDataInfo = null;
    }

    /**
     * FORM 타입 페이지 데이터 초기화 함수
     * save 타입일 경우 기본 content 데이터 유지
     *
     * @param {string|undefined} type 초기화 타입
     */
    seFormTypeClear(type = undefined) {
        if(type === "save"){
            const contentItem = this.currentDataInfo.form.find(item => item.type === "content");
            this.setFormDataUpDate(contentItem.value, contentItem._id)
        }
        this.formDataMap = {};
        this.formFile = [];
    }

    /**
     * TEMPLATE 타입 페이지 데이터 초기화 함수
     */
    setTemplateClear() {
        this.templateHtml = null;
        this.templateTabHtml = null;
        this.templateContentHtml = null;
    }

}

/**
 * ContentStore Hook
 *
 * @returns {ContentStore}
 */
export const useContentStore = () => {
    return useStore().contentStore;
}

/**
 * ContentStore 직접 접근 함수
 *
 * @returns {ContentStore}
 */
export const getContentStore = () => {
    return getStore().contentStore;
}
